import { getConfig, setConfig } from '../configs/config';
import { getPartialMetaData } from '../configs/meta';
import { formComplete, formStart } from '../constants/analytics';
import messages from '../constants/logMessages.json';
import tracking from '../tracking/tracking';
import { EngagementTrackingPayload } from '../types/designSystem';
import {
  EcommerceGenericPurchasePayload,
  EcommercePurchasePayload,
} from '../types/ecommerce';
import {
  AlertErrorPayload,
  BusinessErrorPayload,
  GeneralErrorPayload,
  TechnicalErrorPayload,
  ValidationErrorPayload,
} from '../types/errors';
import {
  AnalyticsEventTypes,
  AnalyticsPageView,
  AnalyticsSelectItemEcommerce,
  AnalyticsStandardEvent,
  AnalyticsViewItemEcommerce,
  AnalyticsViewItemListEcommerce,
  ApiComplete,
  ApiError,
  ApiStart,
  BuyStart,
  EmailQuote,
  EngagementTrackingLegacyPayload,
  IndicativeQuote,
  Purchase,
  QuoteComplete,
  QuoteStart,
  RetrieveQuote,
  TransactionComplete,
  TransactionStart,
  TransactionStartView,
} from '../types/index';
import engagementTracking from '../utils/adsEngagementTracking';
import generateErrorString from '../utils/errors';
import {
  apiMilestoneHandler,
  completeTimeHandler,
  enableSendQALErrorsToDynatrace,
  getApiType,
  getDynatraceGroupString,
  getLoggingMessage,
  getReturnMessage,
  isEmpty,
  jsonToString,
  kpiMilestoneHandler,
  mergeInitialData,
  milestoneHelperHandler,
  omitBy,
  onDtrumReady,
  setPageReferrer,
  startTimeHandler,
  transactionMilestoneHandler,
  transactionMilestonePageViewHandler,
  transformDataLayerToSessionProperties,
  transformObjToDtSessionProperties,
} from '../utils/helpers';
import { isStandalone } from '../utils/helpers';
import { apiErrorResponseConstructor } from '../utils/helpers/apiErrorHelpers';
import { postAnalyticsMessageToShell } from '../utils/helpers/mfeEventBusHelpers';
import logging, { getMessage } from '../utils/logging';
import tools, { removeQueryParams } from '../utils/tools';

const {
  infoHeading,
  infoDtHeading,
  warnHeading,
  errorHeading,
  infoBold,
  infoDtRegular,
  infoRegular,
  warnBold,
  warnRegular,
  errorBold,
} = messages;

/**
 * Retrieves the MFE (Micro Frontend) configuration from the application settings.
 *
 * @returns {object} An object containing:
 *  - mfeType: Type of the MFE (default is an empty string).
 *  - mfeName: Name of the MFE (default is an empty string).
 */
const getMfeConfig = () => {
  const { mfeType = '', mfeName = '' } = getConfig().qalMfeConfig ?? {};
  const { enableQalMfe = false } = getConfig();

  return { mfeType, mfeName, enableQalMfe };
};

/**
 * Handles events in an MFE by sending them to the shell application if conditions are met.
 *
 * @param {string} methodName - The event or method name to be tracked.
 * @param {unknown} payload - The data associated with the event.
 * @returns {boolean} - Returns `true` if the event was handled by the MFE event bus,
 *                      otherwise returns `false` to allow normal tracking.
 */
let logged = false;
const mfeEventHandler = (methodName: string, payload: unknown): boolean => {
  const { mfeType, mfeName, enableQalMfe } = getMfeConfig();

  // If QAL event tracking is enabled, the application is an MFE, and the MFE is embedded in a shell, then process the event
  if (enableQalMfe && mfeType === 'mfe' && !isStandalone) {
    postAnalyticsMessageToShell(
      methodName,
      omitBy(mergeInitialData(payload)), // Merge initial MFE data with payload and remove empty values
      mfeName
    );

    return true; // Event was handled by the MFE event bus
  }

  // Log if MFE is standalone, i.e. when the app is loaded directly in a browser window
  if (!logged && enableQalMfe && mfeType === 'mfe' && isStandalone) {
    logging.warn(
      getMessage('shellNotDetected', [mfeName]),
      warnHeading,
      warnBold,
      warnRegular
    );
    logged = true;

    // Also log a GA event
    tracking.analyticsCustomEvent({
      event: 'error_mfe',
      eventAction: mfeName,
      eventLabel:
        `No shell app detected for ${mfeName}; ` +
        'app opened directly in browser',
    });
  }

  return false; // Event should proceed with normal tracking logic
};

const logDynatraceSessionProperties = (
  shortStringJsonObj: any,
  type: string
) => {
  onDtrumReady(() => {
    // Send the shortStringJsonObj using Dynatrace RUM sendSessionProperties and log the result
    const propertiesSendingReport = window.dtrum.sendSessionProperties(
      undefined, // javaLongOrObject
      undefined, // date
      shortStringJsonObj, // shortString JSON object
      undefined // javaDouble
    );

    // sendSessionProperties returns PropertiesSendingReport object
    logging.info(
      getMessage('dtSessionProperties', [
        type,
        jsonToString(propertiesSendingReport),
      ]),
      infoDtHeading,
      infoRegular
    );
  });
};

const logDynatraceMetaData = (): void => {
  const defaultKeys = [
    'appType',
    'appName',
    'appProduct',
    'appPartner',
    'appVersion',
    'appEnv',
  ];
  const { sendQbeTrackMetaKeysToDynatrace } = getConfig();

  // Exit early if sendQbeTrackMetaKeysToDynatrace is false or an empty array
  if (
    !sendQbeTrackMetaKeysToDynatrace ||
    (typeof sendQbeTrackMetaKeysToDynatrace !== 'boolean' &&
      isEmpty(sendQbeTrackMetaKeysToDynatrace))
  )
    return;

  const meta: Array<string> =
    sendQbeTrackMetaKeysToDynatrace === true
      ? defaultKeys
      : sendQbeTrackMetaKeysToDynatrace;

  const partialMetaDataToSend = getPartialMetaData(meta);

  // Return early if partialMetaDataToSend is empty
  if (!partialMetaDataToSend || isEmpty(partialMetaDataToSend)) return;

  // Transform meta object to Dynatrace Session Properties JSON object
  const transformedData = transformObjToDtSessionProperties(
    partialMetaDataToSend,
    true // send with 'public = true'
  );

  // Return early if partialMetaDataToSend is empty
  if (isEmpty(transformedData)) return;

  // Send payload to Dynatrace
  logDynatraceSessionProperties(transformedData, 'qbeTrack.meta');
};

const logDynatraceStackTrace = (
  error: unknown,
  parentActionId?: number
): void => {
  onDtrumReady(() => {
    // Check if error is an instance of Error
    if (error instanceof Error) {
      // Log Dynatrace reportError with Error object input
      window.dtrum.reportError(error, parentActionId);

      const stackTrace = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };

      logging.info(
        getMessage('stackTraceLogged', [jsonToString(stackTrace)]),
        infoDtHeading,
        infoRegular
      );
    } else if (typeof error === 'object' && error !== null) {
      // Check if error is a generic object and not null
      logging.error(
        getMessage('notAnErrorObject', [jsonToString(error as object)]),
        errorHeading,
        errorBold
      );
    } else {
      // Handle if string
      logging.error(
        getMessage('notAnErrorObject', [String(error)]),
        errorHeading,
        errorBold
      );
    }
  });
};

const logDynatraceStackString = (
  error: string,
  parentActionId?: number
): void => {
  onDtrumReady(() => {
    // Log Dynatrace reportError with string input
    window.dtrum.reportError(error, parentActionId);

    logging.info(
      getMessage('stackTraceLogged', [error]),
      infoHeading,
      infoBold
    );
  });
};

const logDynatraceCustomError = (error: {
  key: string;
  value: string;
  hint?: string;
  parentingInfo?: number | boolean;
}): void => {
  const { key = '', value = '', hint = '', parentingInfo } = error;

  onDtrumReady(() => {
    window.dtrum.reportCustomError(key, value, hint, parentingInfo);

    const parentingInfoStr =
      parentingInfo !== undefined ? String(parentingInfo) : '';

    logging.info(
      getMessage('customErrorLogged', [key, value, hint, parentingInfoStr]),
      infoDtHeading,
      infoRegular,
      infoDtRegular,
      infoRegular,
      infoDtRegular,
      infoRegular,
      infoDtRegular,
      infoRegular,
      infoDtRegular,
      infoRegular
    );
  });
};
// Custom Event including e.g. Form Start.
const trackCustomEvent = (payload: AnalyticsStandardEvent): void => {
  if (mfeEventHandler('trackCustomEvent', payload)) return;

  const mergedData = omitBy(mergeInitialData(payload));
  tracking.analyticsCustomEvent(mergedData);
};

const logDynatracePageView = (virtualPagePath: string): void => {
  const { sendPageViewToDynatrace = false } = getConfig();
  const metaKeysForGroup = ['appPartner', 'appType', 'appName', 'appProduct'];
  const { basePagePath = '' } = getConfig();
  if (!sendPageViewToDynatrace) return;

  const setPagePayload = {
    name: basePagePath + virtualPagePath,
    group: getDynatraceGroupString(metaKeysForGroup),
  };

  onDtrumReady(() => {
    const returnCode = window.dtrum.setPage(setPagePayload);
    const returnMessage = getReturnMessage(returnCode);
    getLoggingMessage(returnCode, returnMessage, setPagePayload);
  });
};

/**
 * Track a virtual page view.
 *
 * @param {AnalyticsPageView} payload - The payload containing page view data.
 */
const trackPageView = (payload: AnalyticsPageView): void => {
  if (mfeEventHandler('trackPageView', payload)) return;

  const { resetMilestoneFormEngagedDefault = false } = getConfig();
  const dlKeyWhitelist = getConfig().sendDataLayerKeysToDynatrace as string[];
  const { virtualPagePath } = payload;
  const {
    formEngaged = false,
    formCompleted = false,
    ...formRest
  } = getConfig().formEngagement;

  // Merge initial data with the provided payload
  const mergedPayload = mergeInitialData(payload);

  // Extract ecommerce data and other payload data
  const { mfeName = '', ...pageViewPayload } = mergedPayload;

  // Send select dataLayer keys to Dynatrace Session Properties
  // If sendDataLayerKeysToDynatrace contains an array of string(s) in analyticsWrapper
  // Needs to be executed before the pushing of a page_view event to the dataLayer
  if (!isEmpty(dlKeyWhitelist)) {
    const transformedData = transformDataLayerToSessionProperties(
      dlKeyWhitelist,
      pageViewPayload.data
    );
    if (!isEmpty(transformedData)) {
      logDynatraceSessionProperties(transformedData, 'dataLayer');
    }
  }

  // Track page views
  tracking.analyticsPageView({ mfeName, ...pageViewPayload });

  // Track Dynatrace page views, if sendPageViewToDynatrace enabled in analyticsWrapper
  logDynatracePageView(virtualPagePath);

  // Handle whether page view should fire a start time
  startTimeHandler(pageViewPayload, mfeName);
  if (resetMilestoneFormEngagedDefault) {
    // Reset formEngaged if customers already interacted with components

    if (formEngaged || formCompleted) {
      setConfig({
        formEngagement: {
          formEngaged: false,
          formCompleted: false,
          ...formRest,
        },
      });
    }
  }

  // Handle whether milestones should be fired
  kpiMilestoneHandler(mergedPayload);

  // Handle Transaction KPI event
  transactionMilestonePageViewHandler(pageViewPayload);

  // Set Previous Page path
  setPageReferrer(virtualPagePath);
};

/**
 * Engagement Tracking.
 * Reserved for Design System Engagement Tracking.
 * @param {*} payload
 */
const trackClick = (payload: AnalyticsStandardEvent): void => {
  if (mfeEventHandler('trackClick', payload)) return;
  const mergedData = omitBy(mergeInitialData(payload));
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Email Quote
 */
const trackEmailQuote = ({
  event = AnalyticsEventTypes.EmailQuote,
  ...restPayload
}: EmailQuote = {}): void => {
  if (
    mfeEventHandler('trackEmailQuote', {
      event,
      ...restPayload,
    })
  )
    return;
  const {
    data: { quoteNumber = '', quoteId = '' } = {} as {
      quoteNumber?: string;
      quoteId?: string;
    },
    ...otherPayload
  } = restPayload;

  // Ensure data.quoteNumber and data.quoteId are present and valid strings
  if (
    (!quoteNumber && !quoteId) ||
    (typeof quoteNumber !== 'string' && typeof quoteId !== 'string')
  ) {
    logging.warn(
      getMessage('noQuoteNumber', [AnalyticsEventTypes.EmailQuote]),
      warnHeading,
      warnBold
    );
    return;
  }

  const updatedData = {
    ...restPayload.data,
    cm_email_quote: 1,
  };
  const trackingPayload = {
    event,
    data: updatedData,
    ...otherPayload,
  };

  const mergedData = omitBy(mergeInitialData(trackingPayload));
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Retrieve Quote
 */
const trackRetrieveQuote = ({
  event = AnalyticsEventTypes.RetrieveQuote,
  ...restPayload
}: RetrieveQuote = {}): void => {
  if (
    mfeEventHandler('trackRetrieveQuote', {
      event,
      ...restPayload,
    })
  )
    return;
  const {
    data: { quoteNumber = '', quoteId = '' } = {} as {
      quoteNumber?: string;
      quoteId?: string;
    },
    ...otherPayload
  } = restPayload;

  // Ensure data.quoteNumber and data.quoteId are present and valid strings
  if (
    (!quoteNumber && !quoteId) ||
    (typeof quoteNumber !== 'string' && typeof quoteId !== 'string')
  ) {
    logging.warn(
      getMessage('noQuoteNumber', [AnalyticsEventTypes.RetrieveQuote]),
      warnHeading,
      warnBold
    );
    return;
  }

  const updatedData = {
    ...restPayload.data,
    cm_retrieve_quote: 1,
  };
  const trackingPayload = {
    event,
    data: updatedData,
    ...otherPayload,
  };

  const mergedData = omitBy(mergeInitialData(trackingPayload));
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Quote Start
 */
const trackQuoteStart = ({
  event = AnalyticsEventTypes.QuoteStart,
  mfeName = '',
  ...restPayload
}: QuoteStart = {}): void => {
  if (
    mfeEventHandler('trackQuoteStart', {
      event,
      ...restPayload,
    })
  )
    return;
  const { data, ...otherPayload } = restPayload;
  const updatedData = {
    ...data,
    cm_quote_start: 1,
  };
  const trackingPayload = {
    event,
    data: updatedData,
    ...otherPayload,
  };

  if (!milestoneHelperHandler(trackingPayload)) return;
  const mergedData = omitBy(mergeInitialData(trackingPayload));
  if (mfeName) {
    mergedData.mfeName = mfeName;
  }
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Quote Complete
 */
const trackQuoteComplete = ({
  event = AnalyticsEventTypes.QuoteComplete,
  ...restPayload
}: QuoteComplete): void => {
  if (
    mfeEventHandler('trackQuoteComplete', {
      event,
      ...restPayload,
    })
  )
    return;
  const { data, mfeName = '', ...otherPayload } = restPayload;
  const updatedData = {
    ...data,
    cm_quote_complete: 1,
  };
  const trackingPayload = {
    event,
    data: updatedData,
    ...otherPayload,
  };
  if (!milestoneHelperHandler(trackingPayload)) return;
  const mergedData = omitBy(
    mergeInitialData(completeTimeHandler(trackingPayload))
  );
  if (mfeName) {
    mergedData.mfeName = mfeName;
  }
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Indicative Quote
 */
const trackIndicativeQuote = ({
  event = AnalyticsEventTypes.IndicativeQuote,
  ...restPayload
}: IndicativeQuote): void => {
  if (
    mfeEventHandler('trackIndicativeQuote', {
      event,
      ...restPayload,
    })
  )
    return;
  const { data, mfeName = '', ...otherPayload } = restPayload;
  const updatedData = {
    ...data,
    cm_indicative_quote: 1,
  };
  const trackingPayload = {
    event,
    data: updatedData,
    ...otherPayload,
  };

  if (!milestoneHelperHandler(trackingPayload)) return;
  const mergedData = omitBy(
    mergeInitialData(completeTimeHandler(trackingPayload))
  );
  if (mfeName) {
    mergedData.mfeName = mfeName;
  }
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Buy Start
 */
const trackBuyStart = ({
  event = AnalyticsEventTypes.BuyStart,
  ...restPayload
}: BuyStart): void => {
  if (
    mfeEventHandler('trackBuyStart', {
      event,
      ...restPayload,
    })
  )
    return;
  const {
    data: { quoteNumber = '', quoteId = '' } = {} as {
      quoteNumber?: string;
      quoteId?: string;
    },
    mfeName = '',
  } = restPayload;

  // Ensure data.quoteNumber and data.quoteId are present and valid strings
  if (
    (!quoteNumber && !quoteId) ||
    (typeof quoteNumber !== 'string' && typeof quoteId !== 'string')
  ) {
    logging.warn(
      getMessage('buyStartError', [AnalyticsEventTypes.BuyStart]),
      warnHeading,
      warnBold
    );
    return;
  }
  const { data, ...otherPayload } = restPayload;
  const updatedData = {
    ...data,
    cm_buy_start: 1,
  };
  const trackingPayload = {
    event,
    data: updatedData,
    ...otherPayload,
  };

  if (!milestoneHelperHandler(trackingPayload)) return;
  const mergedData = omitBy(mergeInitialData(trackingPayload));
  if (mfeName) {
    mergedData.mfeName = mfeName;
  }
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Purchase
 */
const trackPurchase = ({
  event = AnalyticsEventTypes.Purchase,
  eventAction = 'purchase',
  eventLabel = undefined,
  ...restPayload
}: Purchase): void => {
  if (
    mfeEventHandler('trackPurchase', {
      event,
      eventAction,
      eventLabel,
      ...restPayload,
    })
  )
    return;
  const trackingPayload = {
    event,
    eventAction,
    eventLabel,
    ...restPayload,
  };
  const { mfeName = '' } = restPayload;
  if (!milestoneHelperHandler(trackingPayload)) return;
  const mergedData = mergeInitialData(completeTimeHandler(trackingPayload));
  if (mfeName) {
    mergedData.mfeName = mfeName;
  }
  tracking.analyticsEcommercePurchase(mergedData);
};

/**
 * Transaction Start
 */
const trackTransactionStart = ({
  event = AnalyticsEventTypes.TransactionStart,
  eventAction = undefined,
  eventLabel = undefined,
  ...restPayload
}: TransactionStart): void => {
  if (
    mfeEventHandler('trackTransactionStart', {
      event,
      eventAction,
      eventLabel,
      ...restPayload,
    })
  )
    return;
  // Adding the cm_transaction_start key within the data object
  const { data, ...otherPayload } = restPayload;
  const updatedData = {
    ...data,
    cm_transaction_start: 1,
  };
  const trackingPayload = {
    event,
    eventAction,
    eventLabel,
    data: updatedData,
    ...otherPayload,
  };
  if (!transactionMilestoneHandler(trackingPayload)) return;
  const mergedData = omitBy(mergeInitialData(trackingPayload));
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Transaction Complete
 */
const trackTransactionComplete = ({
  event = AnalyticsEventTypes.TransactionComplete,
  eventAction = undefined,
  eventLabel = undefined,
  ...restPayload
}: TransactionComplete): void => {
  if (
    mfeEventHandler('trackTransactionComplete', {
      event,
      eventAction,
      eventLabel,
      ...restPayload,
    })
  )
    return;

  // Adding the cm_transaction_start key within the data object
  const { data, ...otherPayload } = restPayload;
  const updatedData = {
    ...data,
    cm_transaction_complete: 1,
  };
  const trackingPayload = {
    event,
    eventAction,
    eventLabel,
    data: updatedData,
    ...otherPayload,
  };
  if (!transactionMilestoneHandler(trackingPayload)) return;
  const mergedData = omitBy(
    mergeInitialData(completeTimeHandler(trackingPayload))
  );
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * API Start
 */
const trackApiStart = ({
  event = AnalyticsEventTypes.ApiStart,
  ...restPayload
}: ApiStart): void => {
  if (
    mfeEventHandler('trackApiStart', {
      event,
      ...restPayload,
    })
  )
    return;

  const { apiName = '', message = '', apiEndpoint = '' } = restPayload.data;
  const cleanedApiEndpoint = removeQueryParams(apiEndpoint); // Clean the apiEndpoint
  const trackingPayload = {
    event,
    eventAction: apiName,
    eventLabel: message || getApiType(apiName),
    ...restPayload,
  };

  const updatedData: ApiStart['data'] = {
    ...restPayload.data,
    apiEndpoint: cleanedApiEndpoint, // Use cleaned apiEndpoint
  };

  const updatedTrackingPayload: ApiStart = {
    ...trackingPayload,
    data: updatedData,
  };

  if (!apiMilestoneHandler(updatedTrackingPayload)) return;

  // Exit early if enableApiStartEvent from the AnalyticsWrapper is false
  const { enableApiStartEvent = 'true' } = getConfig();
  if (!enableApiStartEvent) return;

  const mergedData = omitBy(mergeInitialData(updatedTrackingPayload));
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * API Complete
 */
const trackApiComplete = ({
  event = AnalyticsEventTypes.ApiComplete,
  ...restPayload
}: ApiComplete): void => {
  if (
    mfeEventHandler('trackApiComplete', {
      event,
      ...restPayload,
    })
  )
    return;
  const { apiName = '', message = '', apiEndpoint = '' } = restPayload.data;
  const cleanedApiEndpoint = removeQueryParams(apiEndpoint); // Clean the apiEndpoint
  const trackingPayload = {
    event,
    eventAction: apiName,
    eventLabel: message || getApiType(apiName),
    ...restPayload,
  };
  const apiResult = apiMilestoneHandler(trackingPayload);
  if (!apiResult) return;

  const timeApiResponse = apiResult;

  // Add timeApiResponse to the data object
  const updatedData = {
    ...(restPayload.data || {}),
    apiEndpoint: cleanedApiEndpoint, // Use cleaned apiEndpoint
    timeApiResponse,
  };

  const updatedTrackingPayload = {
    ...trackingPayload,

    data: updatedData,
  };

  const mergedData = omitBy(mergeInitialData(updatedTrackingPayload));
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Form Value Submit
 * Reserve for Design System Engagement Tracking.
 * @param {*} payload
 */
const trackFormValueSubmit = (payload: AnalyticsStandardEvent): void => {
  if (mfeEventHandler('trackFormValueSubmit', payload)) return;
  const { eventAction } = payload;
  if (!eventAction) {
    throw new Error(
      `Site Tracking invalid: it is required to have ${generateErrorString(
        'Action'
      )}`
    );
  }

  const mergedData = mergeInitialData(payload);
  tracking.analyticsPageView(mergedData);
};

/**
 * Tracks a business error event by merging the initial data and adding a business error indicator.
 *
 * This function takes a BusinessErrorPayload object, checks for any missing required fields (message,
 * questionId, errorCode, errorType), and logs a warning if any are missing. It then tries to merge the
 * initial data with the provided payload, adding a `cm_business_error` flag set to 1. If successful, it
 * sends the updated payload to the `analyticsBusinessError` tracking function. If an error occurs during
 * this process, it logs the error.
 *
 * @param {BusinessErrorPayload} payload - The business error payload containing error details and additional data.
 */

const trackBusinessError = ({
  message = '',
  questionId = '',
  errorCode = '',
  errorType = '',
  data = {},
}: BusinessErrorPayload): void => {
  if (
    mfeEventHandler('trackBusinessError', {
      message,
      questionId,
      errorCode,
      errorType,
      data,
    })
  )
    return;

  const missingDataPoints: string[] = [];

  if (!message) missingDataPoints.push('message');
  if (!questionId) missingDataPoints.push('questionId');
  if (!errorCode) missingDataPoints.push('errorCode');
  if (!errorType) missingDataPoints.push('errorType');

  if (missingDataPoints.length > 0) {
    const missingDataString = missingDataPoints.join(', ');
    logging.warn(
      getMessage('businessData', [missingDataString]),
      warnHeading,
      warnBold
    );
  }

  try {
    const mergedData = mergeInitialData({
      message,
      questionId,
      errorCode,
      errorType,
      data: {
        ...data,
        cm_business_error: 1,
      },
    });
    tracking.analyticsBusinessError(mergedData);

    if (!enableSendQALErrorsToDynatrace('enableTrackBusinessError')) return;
    logDynatraceCustomError({
      key: AnalyticsEventTypes.BusinessError, // event
      value: message, // message
      hint: questionId, // questionId
      parentingInfo: true, // default to true
    });
  } catch (error: any) {
    logging.error(
      getMessage('businessError', [error.message]),
      errorHeading,
      errorBold
    );
  }
};

/**
 * Tracks a technical error event by merging the initial data and adding a technical error indicator.
 *
 * This function takes a TechnicalErrorPayload object, checks for any missing required fields (message,
 * errorCode, errorType), and logs a warning if any are missing. It then tries to merge the initial data
 * with the provided payload, adding a `cm_technical_error` flag set to 1. If successful, it sends the
 * updated payload to the `analyticsTechnicalError` tracking function. If an error occurs during this process,
 * it logs the error.
 *
 * @param {TechnicalErrorPayload} payload - The technical error payload containing error details and additional data.
 */
const trackTechnicalError = ({
  message = '',
  errorCode = '',
  errorType = '',
  data = {},
}: TechnicalErrorPayload): void => {
  if (
    mfeEventHandler('trackTechnicalError', {
      message,
      errorCode,
      errorType,
      data,
    })
  )
    return;
  const missingDataPoints: string[] = [];

  if (!message) missingDataPoints.push('message');
  if (!errorCode) missingDataPoints.push('errorCode');
  if (!errorType) missingDataPoints.push('errorType');

  if (missingDataPoints.length > 0) {
    const missingDataString = missingDataPoints.join(', ');
    logging.warn(
      getMessage('technicalData', [missingDataString]),
      warnHeading,
      warnBold
    );
  }

  try {
    const mergedData = mergeInitialData({
      message,
      errorCode,
      errorType,
      data: {
        ...data,
        cm_technical_error: 1,
      },
    });
    tracking.analyticsTechnicalError(mergedData);

    if (!enableSendQALErrorsToDynatrace('enableTrackTechnicalError')) return;

    logDynatraceCustomError({
      key: AnalyticsEventTypes.TechnicalError, // event
      value: message, // message
      hint: `error code: ${errorCode}`, // string with errorCode
      parentingInfo: true, // default to true
    });
  } catch (error: any) {
    logging.error(
      getMessage('technicalError', [error.message]),
      errorHeading,
      errorBold
    );
  }
};

/**
 * Tracks a validation error event by merging the initial data and adding a validation error indicator.
 *
 * This function takes an ErrorPayload object, updates its data to include a `cm_validation_error`
 * flag set to 1, merges it with the initial data using `mergeInitialData`, and then sends the
 * updated payload to the `analyticsGeneralError` tracking function.
 *
 * @param {ErrorPayload} payload - The error payload containing error details and additional data.
 */
const trackValidationError = (payload: ValidationErrorPayload): void => {
  if (mfeEventHandler('trackValidationError', payload)) return;
  const { message = '', questionId = '', data, ...otherPayload } = payload;
  const updatedData = {
    ...data,
    cm_validation_error: 1,
  };
  const trackingPayload = {
    message,
    questionId,
    ...otherPayload,
    data: updatedData,
  };
  const mergedData = mergeInitialData(trackingPayload);
  tracking.analyticsValidationError(mergedData);

  if (!enableSendQALErrorsToDynatrace('enableTrackValidationError')) return;

  logDynatraceCustomError({
    key: AnalyticsEventTypes.ValidationError, // event
    value: message, // message
    hint: questionId, // questionId
    parentingInfo: true, // default to true
  });
};

/**
 * Tracks a general error event by merging the initial data and adding a general error indicator.
 *
 * This function takes an ErrorPayload object, updates its data to include a `cm_general_error`
 * flag set to 1, merges it with the initial data using `mergeInitialData`, and then sends the
 * updated payload to the `analyticsGeneralError` tracking function.
 *
 * @param {ErrorPayload} payload - The error payload containing error details and additional data.
 */
const trackGeneralError = (payload: GeneralErrorPayload): void => {
  if (mfeEventHandler('trackGeneralError', payload)) return;
  const { message = '', questionId = '', data, ...otherPayload } = payload;
  const updatedData = {
    ...data,
    cm_general_error: 1,
  };
  const trackingPayload = {
    message,
    questionId,
    ...otherPayload,
    data: updatedData,
  };
  const mergedData = mergeInitialData(trackingPayload);
  tracking.analyticsGeneralError(mergedData);

  if (!enableSendQALErrorsToDynatrace('enableTrackGeneralError')) return;
  logDynatraceCustomError({
    key: AnalyticsEventTypes.GeneralError, // event
    value: message, // message
    hint: questionId, // questionId
    parentingInfo: true, // default to true
  });
};

/**
 * Tracks a alert error event by merging the initial data and adding a general error indicator.
 *
 * This function takes an ErrorPayload object, updates its data to include a `cm_alert_error`
 * flag set to 1, merges it with the initial data using `mergeInitialData`, and then sends the
 * updated payload to the `analyticsAlertError` tracking function.
 *
 * @param {ErrorPayload} payload - The error payload containing error details and additional data.
 */
const trackAlertError = (payload: AlertErrorPayload): void => {
  if (mfeEventHandler('trackAlertError', payload)) return;
  const { message = '', data, ...otherPayload } = payload;
  const updatedData = {
    ...data,
    cm_alert_error: 1,
  };
  const trackingPayload = {
    message,
    ...otherPayload,
    data: updatedData,
  };
  const mergedData = mergeInitialData(trackingPayload);
  tracking.analyticsAlertError(mergedData);

  // if (!enableSendQALErrorsToDynatrace('enableTrackGeneralError')) return;
  logDynatraceCustomError({
    key: AnalyticsEventTypes.AlertError, // event
    value: message, // message
    hint: '', // questionId
    parentingInfo: true, // default to true
  });
};

/**
 * Ecommerce Tracking
 * @param {*} payload
 */
const trackEcommercePurchase = (
  payload: EcommercePurchasePayload | EcommerceGenericPurchasePayload
): void => {
  if (mfeEventHandler('trackEcommercePurchase', payload)) return;
  const mergedData = mergeInitialData(payload);
  const updatedMergedData = {
    ...mergedData,
    data: {
      ...mergedData.data,
      cm_purchase: 1,
    },
  };
  tracking.analyticsEcommercePurchase(updatedMergedData);
};

/**
 * Tracks API error events and sends the tracking data to the analytics service.
 *
 * @param {ApiError} payload - The payload containing details about the API error event.
 * @param {AnalyticsEventTypes} payload.event - The event type, defaulting to ApiError.
 * @param {object} payload.data - The data related to the API error.
 * @param {string} payload.data.apiName - The name of the API where the error occurred.
 * @param {string} payload.data.message - The error message.
 * @param {...object} restPayload - Additional properties included in the payload.
 */
const trackApiError = ({
  event = AnalyticsEventTypes.ApiError,
  data,
  ...restPayload
}: ApiError): void => {
  if (mfeEventHandler('trackApiError', { event, data })) return;
  const { apiName = '', message = '', apiEndpoint = '', errorResponse } = data;
  // Remove query parameters from the apiEndpoint using the tools.removeQueryParams function
  const cleanedApiEndpoint = removeQueryParams(apiEndpoint);

  // Construct the initial tracking payload with event, data, and any additional properties
  const trackingPayload = {
    event,
    data,
    ...restPayload,
  };

  // Handle API milestones and get the result
  const apiResult = apiMilestoneHandler(trackingPayload);
  if (!apiResult) return; // Exit if no API milestone result is returned

  // Calculate the API time response from the milestone handler result
  const timeApiResponse = apiResult;

  // Add the time response and error marker to the data object
  const updatedData = {
    ...data,
    apiEndpoint: cleanedApiEndpoint, // Include cleaned endpoint in updated data
    timeApiResponse,
    cm_api_error: 1,
  };

  // Construct the final tracking payload with updated data and other properties
  const updatedTrackingPayload = {
    event,
    eventAction: apiName,
    eventLabel: message || getApiType(apiName),
    data: updatedData,
    ...restPayload,
  };

  // Merge the updated tracking payload with initial data and remove undefined properties
  const mergedData = omitBy(
    mergeInitialData(updatedTrackingPayload)
  ) as ApiError;

  // Send the merged tracking data to the analytics service
  tracking.analyticsApiError(mergedData);

  if (!enableSendQALErrorsToDynatrace('enableTrackApiError')) return;
  const constructedErrorResponse = apiErrorResponseConstructor(
    mergedData,
    errorResponse
  );

  // Log the error event to Dynatrace
  logDynatraceCustomError({
    key: AnalyticsEventTypes.ApiError, // Defines the event type
    value: (
      `x-trackingid:${mergedData.data.requestId}, ` +
      `response: ${constructedErrorResponse}, ` +
      `endpoint: '${cleanedApiEndpoint}'`
    ).trim(), // Includes tracking ID, response details, and API endpoint
    hint: apiName, // API name for reference
    parentingInfo: true, // Ensures error tracking maintains hierarchy
  });
};

const trackEcommerceViewItemList = (
  payload: AnalyticsViewItemListEcommerce
): void => {
  if (mfeEventHandler('trackEcommerceViewItemList', payload)) return;
  const mergedData = mergeInitialData(payload);
  tracking.analyticsEcommerceViewItemList(mergedData);
};

const trackEcommerceSelectItem = (
  payload: AnalyticsSelectItemEcommerce
): void => {
  if (mfeEventHandler('trackEcommerceSelectItem', payload)) return;
  const mergedData = mergeInitialData(payload);
  tracking.analyticsECommerceSelectItemEvent(mergedData);
};

const trackEcommerceViewItem = (payload: AnalyticsViewItemEcommerce): void => {
  if (mfeEventHandler('trackEcommerceViewItem', payload)) return;
  const mergedData = mergeInitialData(payload);
  tracking.analyticsEcommerceViewItem(mergedData);
};

/**
 * Engagement Tracking
 * Reserve for Design System Engagement Tracking.
 * @param {*} payload
 */
const trackEngagement = (payload: EngagementTrackingPayload): void => {
  if (mfeEventHandler('trackEngagement', payload)) return;
  const mergedData = mergeInitialData(payload);

  const { formEngaged = false, ...rest } = getConfig().formEngagement;

  // TODO:
  // We need to identify the ADS submit/next button to avoid triggering form start.
  if (!formEngaged) {
    trackCustomEvent(formStart);
    setConfig({ formEngagement: { formEngaged: true, ...rest } });
  }
  engagementTracking(mergedData);
};

const trackFormStart = (payload: AnalyticsStandardEvent = {}): void => {
  if (mfeEventHandler('trackFormStart', payload)) return;
  const { formEngaged, ...rest } = getConfig().formEngagement;

  if (formEngaged) {
    logging.info(getMessage('formStartAlreadyFired'), infoHeading, infoBold);
    return;
  }
  const { data, ...otherPayload } = payload;

  const updatedData = {
    ...data,
    cm_form_start: 1,
  };
  const updatedPayload = {
    ...otherPayload,
    data: updatedData,
  };

  const mergedData = mergeInitialData(updatedPayload);

  const trackingPayload = {
    ...formStart,
    ...mergedData,
    ...updatedPayload,
  };

  trackCustomEvent(trackingPayload);
  setConfig({ formEngagement: { formEngaged: true, ...rest } });
};

const trackFormComplete = (payload: AnalyticsStandardEvent = {}): void => {
  if (mfeEventHandler('trackFormComplete', payload)) return;
  const { formEngaged, formCompleted, ...rest } = getConfig().formEngagement;

  if (!formEngaged) {
    logging.warn(getMessage('formCompleteNotFired'), warnHeading, warnBold);
    return;
  }

  if (formCompleted) {
    logging.info(getMessage('formCompleteAlreadyFired'), infoHeading, infoBold);
    return;
  }

  const { data, ...otherPayload } = payload;

  const updatedData = {
    ...data,
    cm_form_complete: 1,
  };
  const updatedPayload = {
    ...otherPayload,
    data: updatedData,
  };

  const mergedData = mergeInitialData(updatedPayload);

  const trackingPayload = {
    ...formComplete,
    ...mergedData,
    ...updatedPayload,
  };

  trackCustomEvent(trackingPayload);

  setConfig({ formEngagement: { formCompleted: true, formEngaged, ...rest } });
};

// Track Engagement Tracking event
const trackQalInBuiltEngagementTracking = ({
  event = AnalyticsEventTypes.EngagementTracking,
  ...restPayload
}: EngagementTrackingLegacyPayload): void => {
  if (
    mfeEventHandler('trackQalInBuiltEngagementTracking', {
      event,
      ...restPayload,
    })
  )
    return;

  const mergedData = omitBy(mergeInitialData({ event, ...restPayload }));
  tracking.analyticsCustomEvent(mergedData);
};

// Track Engagement Tracking event
const trackGtmEngagementTracking = (payload: AnalyticsStandardEvent): void => {
  if (mfeEventHandler('trackGtmEngagementTracking', payload)) return;

  const mergedData = omitBy(mergeInitialData(payload));
  tracking.analyticsCustomEvent(mergedData);
};

/**
 * Transaction Start View
 */
const trackTransactionStartView = (
  {
    event = AnalyticsEventTypes.TransactionStartView,
    data = { transactionType: '', ...{} },
    ...restPayload
  }: TransactionStartView = { data: { transactionType: '' } }
): void => {
  if (
    mfeEventHandler('trackTransactionStartView', {
      event,
      data,
      ...restPayload,
    })
  )
    return;

  // Check if transactionType is not set
  const { transactionType } = data;

  const sanitisedTransactionType = transactionType
    .replace(/(?:^\w|[A-Z]|\b\w)/g, (match: string, index: number) =>
      index === 0 ? match.toLowerCase() : match.toUpperCase()
    )
    .replace(/\s+/g, ''); // Remove spaces

  if (!transactionType) {
    // Warn if transactionStartViewTime is not set
    logging.warn(
      getMessage('transactionStartViewTimeNotSet', [sanitisedTransactionType]),
      warnHeading,
      warnBold
    );
    return;
  }

  const updatedData = {
    ...data,
    cm_transaction_start_view: 1,
  };
  const trackingPayload = {
    event,
    data: updatedData,
    ...restPayload,
  };

  const mergedData = omitBy(mergeInitialData(trackingPayload));
  tracking.analyticsCustomEvent(mergedData);

  // Construct the key for transaction start view time

  const transactionStartViewTimeKey = `${sanitisedTransactionType}TransactionStartViewTime`;
  const analyticsTime = tools.handlePersistedConfig()?.analyticsTime || {};
  // Check if the key does not exist in the analyticsTime configuration
  if (analyticsTime && !analyticsTime?.[transactionStartViewTimeKey]) {
    // Set the transaction start view time if it doesn't exist
    setConfig({
      analyticsTime: {
        ...getConfig().analyticsTime,
        [transactionStartViewTimeKey]: tools.getNow(),
      },
    });
  }
};

const analytics = {
  /**
   * Tracks a SPA virtual page view and any page-scoped milestone helper flags.
   *
   * QAL merges configured `initialData`, pushes a `page_view` event, optionally sends selected
   * data layer keys and page names to Dynatrace, stores previous page/referrer state, and evaluates
   * helper flags such as `quoteStartView`, `transactionStartView`, `quoteComplete`, `purchase`,
   * `transactionComplete`, `appStart`, and `appComplete`.
   *
   * When `enableQalMfe` is enabled for an embedded MFE, this call is forwarded to the shell instead
   * of pushing locally.
   *
   * @param payload - Page view payload. `virtualPagePath` is required and is joined with
   * `basePagePath` from `configAnalytics`; `data` should contain only approved analytics values.
   * @returns Nothing. Side effects are data layer pushes, milestone/time updates, and optional
   * Dynatrace/MFE messaging.
   *
   * @example
   * analytics.trackPageView({
   *   virtualPagePath: '/quote/start',
   *   data: {
   *     appStart: true,
   *     quoteStartView: true,
   *   },
   * });
   */
  trackPageView,
  /**
   * Pushes a custom analytics event when no specialised QAL method matches the solution design.
   *
   * Use an approved snake_case event name and avoid raw free text, customer identifiers, backend
   * payloads, stack traces, or query strings. Configured `initialData` is merged into the payload.
   *
   * @param payload - Event payload containing `event`, optional `eventAction`, `eventLabel`, `data`,
   * and additional approved keys.
   * @returns Nothing. The formatted event is pushed to the configured data layer or forwarded to the
   * shell in embedded MFE mode.
   *
   * @example
   * analytics.trackCustomEvent({
   *   event: 'search_results_view',
   *   eventAction: 'repairer_search',
   *   data: { resultCount: 12 },
   * });
   */
  trackCustomEvent,
  /**
   * Tracks a Design System form value submit event.
   *
   * This method is reserved for ADS/Design System integrations and requires `eventAction`.
   *
   * @param payload - Standard event payload. `eventAction` must be present.
   * @returns Nothing.
   * @throws Error when `eventAction` is missing.
   */
  trackFormValueSubmit,
  /**
   * Tracks a business error visible or meaningful to the business journey.
   *
   * Missing `message`, `questionId`, `errorCode`, or `errorType` values are logged as warnings but
   * do not throw. Adds `cm_business_error: 1`, merges `initialData`, pushes a business error event,
   * and sends a Dynatrace custom error when enabled by `sendQALErrorsToDynatrace`.
   *
   * @param payload - Business error payload. Use the user-facing/sanitised message, not raw backend
   * responses or stack traces.
   * @returns Nothing.
   */
  trackBusinessError,
  /**
   * Tracks a technical error such as an application or integration failure.
   *
   * Missing `message`, `errorCode`, or `errorType` values are logged as warnings but do not throw.
   * Adds `cm_technical_error: 1` and optionally sends a Dynatrace custom error.
   *
   * @param payload - Technical error payload. Do not include raw response bodies, stack traces, or PII.
   * @returns Nothing.
   */
  trackTechnicalError,
  /**
   * Tracks a low-level click-style custom event.
   *
   * This method is primarily reserved for Design System engagement tracking. Prefer domain-specific
   * QAL methods for journey milestones.
   *
   * @param payload - Standard event payload for the click interaction.
   * @returns Nothing.
   */
  trackClick,
  /**
   * Tracks Design System engagement and automatically fires `form_start` once per form engagement.
   *
   * The first engagement event in a form sets QAL form engagement state and emits `form_start`.
   * Subsequent engagement events are still processed but do not duplicate `form_start`.
   *
   * @param payload - ADS engagement tracking payload with component metadata in `trackProps`.
   * @returns Nothing.
   */
  trackEngagement,
  /**
   * Tracks a purchase milestone with GA4 ecommerce purchase data.
   *
   * Requires quote and policy identifiers in `data`, and GA4 purchase fields such as `currency`,
   * `transaction_id`, `value`, and `items`. Applies milestone deduplication and completion timing.
   *
   * @param payload - Purchase payload containing QBE data and GA4 ecommerce fields.
   * @returns Nothing.
   *
   * @example
   * analytics.trackPurchase({
   *   data: {
   *     quoteNumber: 'PLOC1003717699',
   *     policyId: 'DI P MVP 7270043',
   *     appComplete: true,
   *   },
   *   currency: 'AUD',
   *   transaction_id: 'DI P MVP 7270043',
   *   value: 1205,
   *   items: [
   *     {
   *       item_name: 'comprehensive',
   *       item_brand: 'QBE Direct',
   *       price: 1205,
   *       quantity: 1,
   *     },
   *   ],
   * });
   */
  trackPurchase,
  /**
   * Tracks a generic GA4 ecommerce purchase event.
   *
   * Use `trackPurchase` for QBE quote-and-buy completion milestones. This lower-level method is for
   * ecommerce payloads that already match the GA4 purchase schema.
   *
   * @param payload - GA4 purchase payload, either directly or under an `ecommerce` key.
   * @returns Nothing.
   */
  trackEcommercePurchase,
  /**
   * Tracks a GA4 `view_item_list` ecommerce event.
   *
   * @param payload - Ecommerce list view payload with `ecommerce.items`.
   * @returns Nothing.
   */
  trackEcommerceViewItemList,
  /**
   * Tracks a GA4 `select_item` ecommerce event.
   *
   * @param payload - Ecommerce select-item payload with `ecommerce.items`.
   * @returns Nothing.
   */
  trackEcommerceSelectItem,
  /**
   * Tracks a GA4 `view_item` ecommerce event.
   *
   * @param payload - Ecommerce item view payload with `ecommerce.items`.
   * @returns Nothing.
   */
  trackEcommerceViewItem,
  /**
   * Tracks the legacy QAL in-built engagement event.
   *
   * Prefer `trackEngagement` for current ADS/Design System integration unless maintaining a legacy
   * reporting contract.
   *
   * @param payload - Legacy engagement payload.
   * @returns Nothing.
   */
  trackQalInBuiltEngagementTracking,
  /**
   * Tracks a GTM-owned engagement event through QAL.
   *
   * Use when GTM/container analytics owns engagement taxonomy but the event still needs QAL metadata
   * merging and MFE forwarding.
   *
   * @param payload - Standard event payload.
   * @returns Nothing.
   */
  trackGtmEngagementTracking,
  /**
   * Tracks a general error event.
   *
   * Adds `cm_general_error: 1`, merges `initialData`, and optionally sends a Dynatrace custom error.
   * Use for errors that are neither business, technical, validation, nor alert errors.
   *
   * @param payload - General error payload with a sanitised message and optional `questionId`.
   * @returns Nothing.
   */
  trackGeneralError,
  /**
   * Tracks a user-visible alert error.
   *
   * Use the message shown to the user, not raw backend errors. Adds `cm_alert_error: 1` and sends a
   * Dynatrace custom error.
   *
   * @param payload - Alert error payload.
   * @returns Nothing.
   */
  trackAlertError,
  /**
   * Tracks the start of a quote journey.
   *
   * Adds `cm_quote_start: 1` and applies QAL milestone deduplication. Call when the user first
   * meaningfully engages with the quote flow, not merely when the page renders.
   *
   * @param payload - Optional quote-start payload and approved `data` values.
   * @returns Nothing.
   */
  trackQuoteStart,
  /**
   * Tracks quote completion.
   *
   * Requires `data.quoteNumber`, adds `cm_quote_complete: 1`, applies milestone deduplication, and
   * calculates quote completion timing when start time is available.
   *
   * @param payload - Quote complete payload containing `data.quoteNumber`.
   * @returns Nothing.
   */
  trackQuoteComplete,
  /**
   * Tracks an indicative quote milestone.
   *
   * Adds `cm_indicative_quote: 1`, applies milestone deduplication, and calculates completion timing
   * when available.
   *
   * @param payload - Indicative quote payload.
   * @returns Nothing.
   */
  trackIndicativeQuote,
  /**
   * Tracks the start of the buy flow after a quote is available.
   *
   * Requires `data.quoteNumber` or `data.quoteId`. Missing identifiers log a warning and prevent the
   * event from firing. Adds `cm_buy_start: 1` and applies milestone deduplication.
   *
   * @param payload - Buy start payload containing quote context.
   * @returns Nothing.
   */
  trackBuyStart,
  /**
   * Tracks the start of an API request.
   *
   * Requires `data.apiName`, `data.apiEndpoint`, and `data.requestId`. Query strings are stripped from
   * `apiEndpoint`. When `enableApiStartEvent` is false, QAL still records timing/deduplication state
   * but does not push `api_start`.
   *
   * @param payload - API start payload.
   * @returns Nothing.
   *
   * @example
   * const requestId = tools.uuid();
   *
   * analytics.trackApiStart({
   *   data: {
   *     apiName: 'quotePrice',
   *     apiEndpoint: 'https://api.example.qbe.com/quote/price',
   *     requestId,
   *   },
   * });
   */
  trackApiStart,
  /**
   * Tracks a transaction start milestone.
   *
   * Requires `data.transactionType`, adds `cm_transaction_start: 1`, and deduplicates by transaction
   * milestone state.
   *
   * @param payload - Transaction start payload.
   * @returns Nothing.
   */
  trackTransactionStart,
  /**
   * Tracks a transaction start view and stores the transaction start-view time.
   *
   * Requires `data.transactionType`. Missing transaction type logs a warning and prevents the event
   * from firing. This method is useful for self-service, claims, or other non-quote journeys.
   *
   * @param payload - Transaction start view payload.
   * @returns Nothing.
   */
  trackTransactionStartView,
  /**
   * Tracks successful completion of an API request.
   *
   * Requires the same `apiName` and `requestId` used for `trackApiStart`. Query strings are stripped
   * from `apiEndpoint`, `timeApiResponse` is added when timing state exists, and duplicate/invalid
   * API milestone state prevents the event from firing.
   *
   * @param payload - API complete payload.
   * @returns Nothing.
   *
   * @example
   * analytics.trackApiComplete({
   *   data: {
   *     apiName: 'quotePrice',
   *     apiEndpoint: 'https://api.example.qbe.com/quote/price',
   *     requestId,
   *   },
   * });
   */
  trackApiComplete,
  /**
   * Tracks a failed API request.
   *
   * Requires `data.apiName`, `data.apiEndpoint`, `data.requestId`, and `data.errorCode`. Query strings
   * are stripped from `apiEndpoint`, `cm_api_error: 1` and `timeApiResponse` are added, and a Dynatrace
   * custom error is sent when enabled.
   *
   * @param payload - API error payload. Keep `errorResponse` sanitised; do not pass raw backend bodies
   * or PII.
   * @returns Nothing.
   *
   * @example
   * analytics.trackApiError({
   *   data: {
   *     apiName: 'quotePrice',
   *     apiEndpoint: 'https://api.example.qbe.com/quote/price?session=redacted',
   *     requestId,
   *     errorCode: '500',
   *     errorType: 'server_error',
   *   },
   * });
   */
  trackApiError,
  /**
   * Tracks transaction completion.
   *
   * Requires `data.transactionType`, adds `cm_transaction_complete: 1`, applies transaction milestone
   * deduplication, and calculates completion timing when start time exists. Set `data.appComplete`
   * when the whole app journey has completed.
   *
   * @param payload - Transaction complete payload.
   * @returns Nothing.
   *
   * @example
   * analytics.trackTransactionComplete({
   *   data: {
   *     transactionType: 'lodgement',
   *     appComplete: true,
   *   },
   * });
   */
  trackTransactionComplete,
  /**
   * Tracks the first form-start milestone.
   *
   * Adds `cm_form_start: 1` and emits `form_start` only once while the current form engagement state
   * remains active. Repeated calls log an informational message and do not duplicate the event.
   *
   * @param payload - Optional form start metadata.
   * @returns Nothing.
   */
  trackFormStart,
  /**
   * Tracks form completion after a form has started.
   *
   * Adds `cm_form_complete: 1`. If no form start has fired, QAL logs a warning and does not emit the
   * completion event. Repeated completion calls are deduplicated.
   *
   * @param payload - Optional form completion metadata.
   * @returns Nothing.
   */
  trackFormComplete,
  /**
   * Tracks successful email/save quote behaviour.
   *
   * Requires `data.quoteNumber` or `data.quoteId`; missing identifiers log a warning and prevent the
   * event from firing. Adds `cm_email_quote: 1`.
   *
   * @param payload - Email quote payload with quote context.
   * @returns Nothing.
   */
  trackEmailQuote,
  /**
   * Tracks successful quote retrieval.
   *
   * Requires `data.quoteNumber` or `data.quoteId`; missing identifiers log a warning and prevent the
   * event from firing. Adds `cm_retrieve_quote: 1`.
   *
   * @param payload - Retrieve quote payload with quote context.
   * @returns Nothing.
   */
  trackRetrieveQuote,
  /**
   * Tracks validation errors, usually for form field validation.
   *
   * Adds `cm_validation_error: 1` and optionally sends a Dynatrace custom error. Prefer controlled
   * field/question identifiers over raw user-entered values.
   *
   * @param payload - Validation error payload with a sanitised message and optional `questionId`.
   * @returns Nothing.
   */
  trackValidationError,
  /**
   * Sends a Dynatrace virtual page view when `sendPageViewToDynatrace` is enabled.
   *
   * @param virtualPagePath - Virtual path, usually the same value passed to `trackPageView`.
   * @returns Nothing.
   */
  logDynatracePageView,
  /**
   * Sends short-string session properties to Dynatrace RUM.
   *
   * @param shortStringJsonObj - Dynatrace short-string JSON object.
   * @param type - Human-readable source label used in logging.
   * @returns Nothing.
   */
  logDynatraceSessionProperties,
  /**
   * Sends configured `qbeTrack.meta` keys to Dynatrace session properties.
   *
   * The sent keys are controlled by `sendQbeTrackMetaKeysToDynatrace`; `true` sends the default app
   * metadata keys.
   *
   * @returns Nothing.
   */
  logDynatraceMetaData,
  /**
   * Reports an `Error` object to Dynatrace RUM.
   *
   * Non-`Error` values are logged as QAL errors and are not reported as stack traces.
   *
   * @param error - Error object to report.
   * @param parentActionId - Optional Dynatrace parent action id.
   * @returns Nothing.
   */
  logDynatraceStackTrace,
  /**
   * Reports a string error message to Dynatrace RUM.
   *
   * @param error - Sanitised string error.
   * @param parentActionId - Optional Dynatrace parent action id.
   * @returns Nothing.
   */
  logDynatraceStackString,
  /**
   * Reports a custom error to Dynatrace RUM.
   *
   * @param error - Dynatrace custom error fields: `key`, `value`, optional `hint`, and optional
   * `parentingInfo`.
   * @returns Nothing.
   */
  logDynatraceCustomError,
};

export default analytics;
